/**
 * Doctor review helpers.
 *
 * - Every doctor shows at least 5 reviews. Real (admin-approved) reviews from
 *   the API are always shown first and are never replaced; the list is only
 *   topped up with the sample reviews below until there are 5.
 * - Every doctor gets a DIFFERENT set of sample reviews (names, wording,
 *   dates) - picked deterministically from the doctor's id/slug so they stay
 *   the same on every page load.
 * - Dr. Shreevarma, Dr. Gowthaman and Dr. Jeyaroopa always get 5-star
 *   reviews. All other doctors get 3 to 4 star reviews.
 * - The star rating on the home page / doctor list / doctor details page all
 *   come from the same function (getDoctorRatingSummary) so they never
 *   disagree with each other.
 */

export const MIN_REVIEWS = 5;

const FIVE_STAR_DOCTORS = ["shreevarma", "gowthaman", "jeyaroopa"];

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");

/** Returns 0, 1, 2 for the three 5-star doctors, or -1 for everyone else. */
const fiveStarIndex = (doctor) => {
  const name = norm(doctor?.name);
  const slug = norm(doctor?.slug);
  return FIVE_STAR_DOCTORS.findIndex((k) => name.includes(k) || slug.includes(k));
};

export const isFiveStarDoctor = (doctor) => fiveStarIndex(doctor) !== -1;

const hash = (str) => {
  let h = 2166136261;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h >>> 0);
};

const doctorKey = (doctor) => `${doctor?.id ?? ""}|${doctor?.slug ?? ""}|${doctor?.name ?? ""}`;

/* ── Reviewer names ── */
const FIRST = [
  "Anand", "Priya", "Karthik", "Meena", "Suresh", "Lakshmi", "Rajesh", "Divya", "Arun", "Kavitha",
  "Vignesh", "Sangeetha", "Manoj", "Revathi", "Prakash", "Deepa", "Ganesh", "Nithya", "Balaji", "Anitha",
  "Harish", "Swathi", "Mohan", "Bhuvana", "Senthil", "Janani", "Ramesh", "Pooja", "Ashwin", "Sowmya",
  "Vijay", "Geetha", "Naveen", "Shalini", "Dinesh", "Uma", "Sathish", "Radhika", "Kumar", "Malathi",
];
const LAST = [
  "Kumar", "Raman", "Iyer", "Nair", "Reddy", "Krishnan", "Menon", "Pillai", "Subramanian", "Narayanan",
  "Sharma", "Rao", "Gopal", "Murugan", "Venkat", "Devi", "Natarajan", "Chandran", "Prasad", "Balan",
];

/* ── Review wording ── */
const FIVE_STAR_COMMENTS = [
  "Excellent consultation. The doctor explained my condition in detail and the treatment plan worked very well.",
  "Very knowledgeable and patient. I felt heard from the first minute and my joint pain has reduced a lot.",
  "Best Ayurvedic consultation I have had so far. Clear advice on diet, lifestyle and medicines.",
  "Genuinely caring doctor. He took time to understand my history before suggesting anything.",
  "Highly recommended! I saw a real improvement within a few weeks of following the prescribed course.",
  "Very professional video consultation. No rush, every doubt was answered patiently.",
  "The treatment is natural and effective. My digestion problems are almost gone now.",
  "Wonderful experience. The doctor is humble, experienced and gives practical advice that is easy to follow.",
  "My whole family consults here. Always accurate diagnosis and gentle, effective medicines.",
  "Super helpful follow-up support. Thank you doctor for the guidance, I am sleeping much better now.",
  "Outstanding doctor. The explanation about root cause and not just symptoms really impressed me.",
  "Booked online and the consultation started on time. Diet chart provided was simple and effective.",
  "I had been suffering for years with back pain. After this treatment I feel far better. Grateful!",
  "Truly a healer. Calm, kind and very experienced in traditional Ayurveda.",
  "Great results with skin problem that other treatments could not solve. Thank you!",
  "Detailed, honest and friendly. He never pushed unnecessary medicines.",
  "Wonderful doctor with great clarity. My energy levels and appetite have improved.",
  "Clear communication and a personalised plan. I would happily recommend to friends and family.",
  "Very satisfied with the consultation. The herbal medicines suggested suited me well.",
  "Amazing care and attention. Felt like a proper in-person visit even on video.",
];

const FOUR_STAR_COMMENTS = [
  "Good consultation. The doctor listened well and the medicines are helping, though progress is gradual.",
  "Nice experience overall. Explanation was clear and I am seeing slow but steady improvement.",
  "Doctor was polite and knowledgeable. Slightly long waiting time but the advice was worth it.",
  "Helpful advice on diet and lifestyle. I would have liked a little more time for questions.",
  "Good treatment approach. My symptoms have reduced, will continue for another course.",
  "Satisfied with the video consultation. Audio dropped once but the doctor handled it well.",
  "Professional and friendly. The prescribed oil and tablets have given me decent relief.",
  "Good doctor, explains things simply. Follow-up response was a little slow, otherwise great.",
  "Genuine Ayurvedic approach without any pressure. I feel better than before.",
  "Happy with the consultation. The plan was practical and easy to follow at home.",
  "Nice and patient doctor. Results are good so far, hoping for full recovery soon.",
  "Clear diagnosis and sensible medicines. Booking process could be a little faster.",
  "Good experience. Took a few weeks to see the effect but it is working now.",
  "Doctor understood my problem quickly and suggested a balanced plan. Recommended.",
  "Solid consultation with useful tips. I appreciated the honest expectations about recovery time.",
  "Pleasant interaction and good guidance. My stomach issues have eased considerably.",
];

const THREE_STAR_COMMENTS = [
  "Average experience. Advice was fine but I expected a more detailed explanation.",
  "Consultation was okay. Medicines are helping a little, need more time to judge.",
  "Decent doctor but the session felt a bit short. Hoping the follow-up is more detailed.",
  "Fair experience. The diet suggestions were useful, results are yet to show fully.",
  "Okay overall. Doctor was polite, though I had to wait past my slot time.",
  "Reasonable consultation. Some improvement in my symptoms but not dramatic so far.",
  "Not bad. The approach is good but I would like clearer instructions on dosage timing.",
  "Middle of the road experience. Helpful in parts, will see how the course works out.",
  "Consultation was fine and the doctor was courteous. Connection issues cut into the time.",
  "Fairly satisfied. Basic guidance was good but I expected a little more personalised advice.",
  "Acceptable experience. Relief has been slow, but the doctor advised patience.",
  "Doctor is knowledgeable, though the explanation was rushed at the end.",
];

/* Different rating patterns for "other" doctors - all average between 3.0 and 4.0. */
const OTHER_PATTERNS = [
  [4, 4, 3, 4, 3], // 3.6
  [4, 3, 4, 4, 4], // 3.8
  [3, 4, 3, 3, 4], // 3.4
  [4, 4, 4, 3, 4], // 3.8
  [3, 3, 4, 4, 3], // 3.4
  [4, 3, 3, 4, 4], // 3.6
  [3, 4, 4, 3, 3], // 3.4
  [4, 4, 3, 3, 4], // 3.6
];

/* Days-ago offsets so each review has a different, recent-looking date. */
const DAY_OFFSETS = [4, 13, 27, 45, 71, 96, 120, 150];

const pick = (arr, n) => arr[((n % arr.length) + arr.length) % arr.length];

/** Builds the five sample reviews for one doctor (deterministic). */
export const buildSampleReviews = (doctor, count = MIN_REVIEWS) => {
  if (!doctor) return [];
  const key = doctorKey(doctor);
  const h = hash(key);
  const fiveIdx = fiveStarIndex(doctor);

  let ratings;
  if (fiveIdx !== -1) ratings = Array(count).fill(5);
  else ratings = OTHER_PATTERNS[h % OTHER_PATTERNS.length].slice(0, count);

  const out = [];
  const fourCursor = h % FOUR_STAR_COMMENTS.length;
  const threeCursor = (h >> 3) % THREE_STAR_COMMENTS.length;
  let four = 0;
  let three = 0;

  for (let i = 0; i < count; i++) {
    const rating = ratings[i] ?? 4;
    let comment;
    if (rating === 5) {
      // Three 5-star doctors take separate slices of the pool, so they never share a comment.
      comment = pick(FIVE_STAR_COMMENTS, fiveIdx * 6 + i);
    } else if (rating === 4) {
      comment = pick(FOUR_STAR_COMMENTS, fourCursor + four * 5);
      four++;
    } else {
      comment = pick(THREE_STAR_COMMENTS, threeCursor + three * 5);
      three++;
    }

    const nameSeed = hash(`${key}#${i}`);
    const first = pick(FIRST, nameSeed);
    let last = pick(LAST, nameSeed >> 5);
    if (last === first) last = pick(LAST, (nameSeed >> 5) + 1);
    const reviewer = `${first} ${last}`;
    const days = pick(DAY_OFFSETS, i + (h % 3)) + (h % 5);
    const created = new Date(Date.now() - days * 24 * 60 * 60 * 1000);

    out.push({
      id: `sample-${doctor?.id ?? h}-${i}`,
      reviewer,
      rating,
      comment,
      created_at: created.toISOString(),
      isSample: true,
    });
  }
  return out;
};

/**
 * Real reviews first (unchanged), then sample reviews to reach MIN_REVIEWS.
 */
export const getDoctorReviewsWithSamples = (doctor, realReviews = []) => {
  const real = Array.isArray(realReviews) ? realReviews : [];
  if (!doctor || real.length >= MIN_REVIEWS) return real;
  const need = MIN_REVIEWS - real.length;
  const samples = buildSampleReviews(doctor, MIN_REVIEWS).slice(0, need);
  return [...real, ...samples];
};

/**
 * Rating + count used everywhere (home, doctor list, doctor details).
 * `realAverage` / `realCount` are the values the API already gives us.
 */
export const getDoctorRatingSummary = (doctor, realAverage, realCount) => {
  const rc = Number(realCount ?? doctor?.review_count ?? 0) || 0;
  const ra = Number(realAverage ?? doctor?.average_rating ?? 0) || 0;
  const merged = getDoctorReviewsWithSamples(
    doctor,
    Array.from({ length: rc }, () => ({ rating: ra }))
  );
  const total = merged.length;
  const sum = merged.reduce((acc, r) => acc + Number(r.rating || 0), 0);
  const average = total ? Math.round((sum / total) * 10) / 10 : 0;
  return { average, count: total };
};

/**
 * Default picture for reviewers ("No Image Available"). Uses the same round
 * avatar placeholder already shown for product reviews (not the wide
 * rectangular "No Image Available" graphic, which is meant for content
 * photos and looks broken when cropped into a circular avatar slot).
 * Root-absolute path so it resolves on every route (a relative path breaks
 * on /doctordetails/...).
 */
export const DEFAULT_AVATAR = "/front/assets/images/avatar.png";

/** Inline fallback, used only if the image file above is ever missing. */
export const DEFAULT_AVATAR_FALLBACK =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"><defs><clipPath id="c"><circle cx="200" cy="200" r="200"/></clipPath></defs><circle cx="200" cy="200" r="200" fill="#e2ece6"/><g clip-path="url(#c)" fill="#2f4f40"><circle cx="200" cy="150" r="60"/><ellipse cx="200" cy="415" rx="130" ry="145"/></g></svg>'
  );

const resolveImagePath = (raw, apiBaseUrl) => {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:") || raw.startsWith("/")) return raw;
  return `${apiBaseUrl}public/profileimage/${raw}`;
};

/**
 * Resolves the image to show for one doctor review's reviewer.
 * - Sample (filler) reviews never carry a photo, so they always fall back
 *   to DEFAULT_AVATAR, same as before.
 * - A real review now comes back from `api/doctor-reviews/:id` with the
 *   reviewer's own uploaded profile photo under `profile_image` (added
 *   server-side, bound via the review's existing user relationship) - a
 *   bare filename resolved the same way Myprofile.js resolves `profileimage`
 *   (served from `public/profileimage/<filename>`), or already a full URL.
 */
export const getDoctorReviewerAvatarUrl = (review, apiBaseUrl = "") => {
  if (!review || review.isSample) return DEFAULT_AVATAR;
  const raw = review.profile_image || review.profileimage || review.user?.profileimage || null;
  return resolveImagePath(raw, apiBaseUrl) || DEFAULT_AVATAR;
};